import { Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Illustration, PartnerLogo } from '@eti/components';
import type { SnowplowEventDataType } from '../../pages/order-details/models';
import ClickableCard from './ClickableCard';

const descriptionStyles = css`
  flex: 1;
  line-height: 21px;
`;

const fullHeightStyles = css`
  color: inherit;
  height: 100%;
  text-decoration: none;
`;

const contentStyles = css`
  flex: 1;
  padding: 16px;
`;

const illustrationStyles = css`
  [dir='rtl'] & {
    transform: rotateY(180deg);
  }
`;

const illustrationWrapperStyles = css`
  align-self: stretch;
  background-color: ${vars.colors.promotional.light};
  border-end-start-radius: inherit;
  border-start-start-radius: inherit;
  padding: 0 8px;
`;

const logoStyles = css`
  width: 70px;
`;

const partnerStyles = css`
  color: #737373;
  font-size: 0.75rem;
  line-height: 18px;
`;

export interface BannerProps {
  'data-testid'?: string;
  description: string;
  iconName: string;
  partnerLogoId: string;
  partnerUrl: string;
  snowplowEventData?: SnowplowEventDataType;
  title: string;
}

const PartnerBanner = ({
  'data-testid': dataTestId,
  description,
  iconName,
  partnerLogoId,
  partnerUrl,
  snowplowEventData,
  title,
}: BannerProps) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const onBannerClick = () => {
    window.open(partnerUrl, '_blank');
  };

  const handleSnowplowOnBanner = () => {
    if (
      isSnowplowTrackingEnabled &&
      snowplowEventData &&
      snowplowEventData.category &&
      snowplowEventData.action &&
      snowplowEventData.label
    ) {
      sendTrackEvent({
        category: snowplowEventData.category,
        action: snowplowEventData.action,
        label: snowplowEventData.label,
      });
    }
  };

  return (
    <ClickableCard
      aria-label={`${title}, ${t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text')}`}
      as="a"
      className={fullHeightStyles}
      data-testid={dataTestId}
      href={partnerUrl}
      onCardSelection={() => {
        onBannerClick();
        handleSnowplowOnBanner();
      }}
      rel="noreferrer"
      target="_blank"
    >
      <Inline className={fullHeightStyles} noWrap spacing={0}>
        <Inline align="center" alignY="center" className={illustrationWrapperStyles}>
          <Illustration
            brand={brand.code}
            className={illustrationStyles}
            data-testid="partner-banner-illustration"
            height={123}
            id={iconName}
          />
        </Inline>
        <Stack className={contentStyles} spacing={4}>
          <Heading as="h2" data-testid="partner-banner-heading" level={4}>
            {title}
          </Heading>
          <Text className={descriptionStyles} data-testid="partner-banner-description">
            {description}
          </Text>
          <Inline
            alignY="center"
            className={partnerStyles}
            data-testid="partner-banner-partner"
            spacing={4}
          >
            <Text>{t('MyBookingsWelcomePage.Banners.ProvidedBy.Text')}</Text>
            <PartnerLogo className={logoStyles} collection="partners" id={partnerLogoId} />
          </Inline>
        </Stack>
      </Inline>
    </ClickableCard>
  );
};

export default PartnerBanner;
